import React, { useState } from 'react';
import { FaUser, FaLock, FaSave, FaArrowLeft, FaArrowRight, FaExclamationCircle } from 'react-icons/fa';
import PinInput from './PinInput';

const KeychainSetup = ({ onSetupSubmit, onCancel, loading, error, translations: t }) => {
    // --- State Management ---
    const [step, setStep] = useState(1); // 1 for PIN, 2 for credentials
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [stepError, setStepError] = useState('');

    // --- PIN Validation Logic ---
    const validatePinStrength = (pinToValidate) => {
        // Rule 1: At least 3 unique digits
        if (new Set(pinToValidate).size < 3) {
            return t.pinUniqueDigitsError;
        }

        // Rule 2 & 3: Check for invalid sequences of 3 digits
        for (let i = 0; i < pinToValidate.length - 2; i++) {
            const d1 = parseInt(pinToValidate[i], 10);
            const d2 = parseInt(pinToValidate[i + 1], 10);
            const d3 = parseInt(pinToValidate[i + 2], 10);

            // Rule 2: No more than 2 consecutive identical digits (e.g., 111)
            if (d1 === d2 && d2 === d3) {
                return t.pinConsecutiveIdenticalError;
            }

            // Rule 3: No more than 2 consecutive sequential digits (e.g., 123 or 321)
            if ((d2 === d1 + 1 && d3 === d2 + 1) || (d2 === d1 - 1 && d3 === d2 - 1)) {
                return t.pinConsecutiveSequentialError;
            }
        }
        return null; // PIN is valid
    };

    // --- Handlers ---
    const handleProceed = () => {
        setStepError('');
        if (pin.length !== 6 || confirmPin.length !== 6) {
            setStepError(t.pinIncompleteError);
            return;
        }
        if (pin !== confirmPin) {
            setStepError(t.pinMismatchError);
            setConfirmPin(''); // Clear only the confirmation field on mismatch
            return;
        }
        
        // Validate PIN strength
        const validationError = validatePinStrength(pin);
        if (validationError) {
            setStepError(validationError);
            // Clear both fields for a fresh attempt on validation failure
            setPin('');
            setConfirmPin('');
            return;
        }

        // All checks passed, move to the next step
        setStep(2);
    };

    const handleFinalSubmit = (e) => {
        e.preventDefault();
        onSetupSubmit({ pin, username, password });
    };

    const handleBack = () => {
        setStepError('');
        setStep(1);
    };

    // --- Render Functions for Each Step ---
    const renderPinStep = () => (
        <>
            <h2 className="login-welcome">{t.createPinTitle}</h2>
            <p className="login-subtitle">{t.createPinSubtitle}</p>
            
            {(error || stepError) && (
                <div className="error-message">
                    <FaExclamationCircle size={18} />
                    <span>{error || stepError}</span>
                </div>
            )}

            <div className="form-group" style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', textAlign: "center" }}>{t.createPinLabel}</label>
                <PinInput pin={pin} setPin={setPin} />
            </div>

            <div className="form-group">
                <label style={{ display: 'block', textAlign: "center" }}>{t.confirmPinLabel}</label>
                <PinInput pin={confirmPin} setPin={setConfirmPin} />
            </div>

            <div style={{ marginTop: '30px' }}>
                <button type="button" className="login-button-expanded" onClick={handleProceed} disabled={loading}>
                    <span>{t.nextBtn}</span>
                    <FaArrowRight size={18} style={{ marginLeft: '8px' }}/>
                </button>
                <button type="button" className="qr-login-button" onClick={onCancel} disabled={loading} style={{ marginTop: '10px' }}>
                    {t.skipKeychainBtn}
                </button>
            </div>
        </>
    );

    const renderCredentialsStep = () => (
        <form className="login-form-expanded" onSubmit={handleFinalSubmit}>
            <h2 className="login-welcome">{t.credentialsTitle}</h2>
            <p className="login-subtitle">{t.credentialsSubtitle}</p>
            
            {error && (
                <div className="error-message"><FaExclamationCircle size={18} /><span>{error}</span></div>
            )}

            <div className="form-group">
                <label htmlFor="setup-username">{t.username}</label>
                <div className="input-with-icon">
                    <FaUser size={16} className="input-icon" />
                    <input id="setup-username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="login-input-expanded" required autoComplete="username" />
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="setup-password">{t.password}</label>
                <div className="input-with-icon">
                    <FaLock size={16} className="input-icon" />
                    <input id="setup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="login-input-expanded" required autoComplete="current-password" />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="qr-login-button" style={{ flex: 1 }} onClick={handleBack} disabled={loading}>
                    <FaArrowLeft size={16} style={{ marginRight: '8px' }} />
                    <span>{t.backBtn}</span>
                </button>
                <button type="submit" className="login-button-expanded" style={{ flex: 2 }} disabled={loading}>
                    {loading ? <span className="spinner" /> : <><FaSave size={18} /><span>{t.saveAndLoginBtn}</span></>}
                </button>
            </div>
        </form>
    );

    return (
        <div>
            {step === 1 ? renderPinStep() : renderCredentialsStep()}
        </div>
    );
};

export default KeychainSetup;